

import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import "../css/gameEdit.css";
const CATEGORY_OPTIONS = [
    { value: 'religion', label: 'Religion' },
    { value: 'topic', label: 'Topic' },
    { value: 'argument', label: 'Argument' },
];

// Helper: find any node (folder or card) by id
function findNodeById(node, id) {
    if (!node) return null;
    if (node.id === id) return node;
    if (!node.children) return null;
    for (const child of node.children) {
        const found = findNodeById(child, id);
        if (found) return found;
    }
    return null;
}

// Helper: find a folder by id
function findFolderById(root, id) {
    const node = findNodeById(root, id);
    if (node && node.type === 'folder') return node;
    return null;
}

// Helper: remove an item from the tree and return it
function removeItemFromTree(node, itemId) {
    if (!node || !node.children) return null;

    const idx = node.children.findIndex((child) => child.id === itemId);
    if (idx !== -1) {
        const [removed] = node.children.splice(idx, 1);
        return removed;
    }

    for (const child of node.children) {
        if (child.type === 'folder') {
            const removed = removeItemFromTree(child, itemId);
            if (removed) return removed;
        }
    }

    return null;
}

// Helper: add an item to a folder
function addItemToFolder(node, folderId, item) {
    if (!node) return false;
    if (node.id === folderId && node.type === 'folder') {
        if (!node.children) node.children = [];
        node.children.push(item);
        return true;
    }
    if (!node.children) return false;
    for (const child of node.children) {
        if (child.type === 'folder') {
            const added = addItemToFolder(child, folderId, item);
            if (added) return true;
        }
    }
    return false;
}

// Helper: update an item in the tree in place (used on a cloned tree)
function updateItemInTree(node, itemId, updater) {
    if (!node) return;
    if (node.id === itemId) {
        const updatedFields = updater(node);
        Object.assign(node, updatedFields);
        return;
    }
    if (!node.children) return;
    for (const child of node.children) {
        updateItemInTree(child, itemId, updater);
    }
}

// Helper: check if target folder is a descendant of the dragged node (to prevent illegal moves)
function isDescendant(root, ancestorId, possibleDescendantId) {
    const ancestor = findNodeById(root, ancestorId);
    if (!ancestor || !ancestor.children) return false;
    const stack = [...ancestor.children];
    while (stack.length) {
        const current = stack.pop();
        if (current.id === possibleDescendantId) return true;
        if (current.children) stack.push(...current.children);
    }
    return false;
}

const initialRoot = {
    id: 'root',
    type: 'folder',
    title: 'Game Decks',
    category: null,
    children: [],
};

function GameEdit() {
    const db = getFirestore();

    const saveTreeToFirestore = async (newTree) => {
        try {
            await setDoc(doc(db, 'gameMeta', 'decksTree'), { tree: newTree });
        } catch (err) {
            console.error('Error saving game tree to Firestore', err);
        }
    };

    const [tree, setTree] = useState(initialRoot);
    const [folderStack, setFolderStack] = useState(['root']); // breadcrumb of folder ids
    const [draggingId, setDraggingId] = useState(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('folder'); // 'folder' | 'card'
    const [editingId, setEditingId] = useState(null); // null = creating new
    const [formValues, setFormValues] = useState({
        title: '', // used ONLY for folders
        category: 'religion', // used ONLY for folders
        reference: '',
        verse: '',
        fakeVerse: '',
        paraphrase: '',
        hint: '',
    });

    useEffect(() => {
        const loadTree = async () => {
            try {
                const snap = await getDoc(doc(db, 'gameMeta', 'decksTree'));
                if (snap.exists()) {
                    const data = snap.data();
                    if (data && data.tree) {
                        setTree(data.tree);
                    }
                }
            } catch (err) {
                console.error('Error loading game tree from Firestore', err);
            }
        };

        loadTree();
    }, [db]);

    const currentFolderId = folderStack[folderStack.length - 1];
    const currentFolder = findFolderById(tree, currentFolderId) || tree;

    const handleOpenFolder = (folderId) => {
        setFolderStack((prev) => [...prev, folderId]);
    };

    const handleBack = () => {
        setFolderStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    };

    const openCreateModal = (mode) => {
        setModalMode(mode);
        setEditingId(null);
        setFormValues({
            title: '',
            category: 'religion',
            reference: '',
            verse: '',
            fakeVerse: '',
            paraphrase: '',
            hint: '',
        });
        setModalOpen(true);
    };

    const openEditModal = (item) => {
        setModalMode(item.type);
        setEditingId(item.id);

        if (item.type === 'folder') {
            setFormValues((prev) => ({
                ...prev,
                title: item.title || '',
                category: item.category || 'religion',
                reference: '',
                verse: '',
                fakeVerse: '',
                paraphrase: '',
                hint: '',
            }));
        } else {
            setFormValues((prev) => ({
                ...prev,
                reference: item.reference || '',
                verse: item.verse || '',
                fakeVerse: item.fakeVerse || '',
                paraphrase: item.paraphrase || '',
                hint: item.hint || '',
            }));
        }

        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setTree((prevTree) => {
            const cloned = JSON.parse(JSON.stringify(prevTree));

            if (editingId) {
                // Update existing item
                updateItemInTree(cloned, editingId, (item) => {
                    if (modalMode === 'folder') {
                        return {
                            ...item,
                            title: formValues.title.trim() || item.title,
                            category: formValues.category,
                        };
                    }
                    // card
                    return {
                        ...item,
                        reference: formValues.reference.trim(),
                        verse: formValues.verse,
                        fakeVerse: formValues.fakeVerse,
                        paraphrase: formValues.paraphrase,
                        hint: formValues.hint,
                    };
                });
            } else {
                // Create new item
                const id = `${modalMode}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                if (modalMode === 'folder') {
                    const newFolder = {
                        id,
                        type: 'folder',
                        title: formValues.title.trim() || 'Untitled Folder',
                        category: formValues.category,
                        children: [],
                    };
                    addItemToFolder(cloned, currentFolderId, newFolder);
                } else {
                    const newCard = {
                        id,
                        type: 'card',
                        reference: formValues.reference.trim(),
                        verse: formValues.verse,
                        fakeVerse: formValues.fakeVerse,
                        paraphrase: formValues.paraphrase,
                        hint: formValues.hint,
                    };
                    addItemToFolder(cloned, currentFolderId, newCard);
                }
            }

            // Persist updated tree to Firestore
            saveTreeToFirestore(cloned);

            return cloned;
        });

        closeModal();
    };

    const handleDragStart = (itemId) => {
        setDraggingId(itemId);
    };

    const handleDragEnd = () => {
        setDraggingId(null);
    };

    const handleFolderDragOver = (e) => {
        e.preventDefault();
    };

    const handleDropOnFolder = (e, targetFolderId) => {
        e.preventDefault();
        if (!draggingId || draggingId === targetFolderId) return;

        setTree((prevTree) => {
            // Prevent dropping a folder into its own descendant
            if (isDescendant(prevTree, draggingId, targetFolderId)) {
                return prevTree;
            }

            const cloned = JSON.parse(JSON.stringify(prevTree));
            const removed = removeItemFromTree(cloned, draggingId);
            if (!removed) return prevTree;

            addItemToFolder(cloned, targetFolderId, removed);
            // Persist updated tree to Firestore after drag-and-drop
            saveTreeToFirestore(cloned);
            return cloned;
        });

        setDraggingId(null);
    };

    const handleDeleteItem = (itemId) => {
        const confirmDelete = window.confirm('Are you sure you want to delete this item? This cannot be undone.');
        if (!confirmDelete) return;

        setTree((prevTree) => {
            const cloned = JSON.parse(JSON.stringify(prevTree));

            // Don't allow deleting the root
            if (itemId === 'root') {
                return prevTree;
            }

            const removed = removeItemFromTree(cloned, itemId);
            if (!removed) {
                return prevTree;
            }

            // Persist updated tree
            saveTreeToFirestore(cloned);

            return cloned;
        });
    };

    // Move the currently edited item up one level in the folder stack
    const handleMoveUpOneLevel = () => {
        if (!editingId || folderStack.length < 2) return;

        const targetFolderId = folderStack[folderStack.length - 2];

        setTree((prevTree) => {
            const cloned = JSON.parse(JSON.stringify(prevTree));

            // Remove the item from wherever it currently lives
            const removed = removeItemFromTree(cloned, editingId);
            if (!removed) {
                return prevTree;
            }

            // Add it to the parent folder (one level up)
            addItemToFolder(cloned, targetFolderId, removed);

            // Persist updated tree
            saveTreeToFirestore(cloned);

            return cloned;
        });

        // Close modal after moving
        setModalOpen(false);
        setEditingId(null);
    };

    const breadcrumb = folderStack
        .map((id) => {
            const folder = findFolderById(tree, id);
            return folder ? folder.title : 'Folder';
        })
        .join(' / ');

    // Compute parent folder for move up one level button
    const parentFolderId = folderStack.length > 1 ? folderStack[folderStack.length - 2] : null;
    const parentFolder = parentFolderId ? findFolderById(tree, parentFolderId) : null;

    return (
        <>
            <header className="game-edit-navbar">
                <div className="game-edit-navbar-left">
                    <div className="game-edit-navbar-title">Catholic Defense Hub</div>
                    <div className="game-edit-navbar-subtitle">Game Deck Editor</div>
                </div>
                <div className="game-edit-navbar-right">
                    <button
                        type="button"
                        className="game-edit-navbar-button"
                        onClick={() => {
                            window.location.href = '/';
                        }}
                    >
                        Home
                    </button>
                </div>
            </header>
            <div className="game-edit-container">
                <div className="game-edit-header">
                    <div className="game-edit-breadcrumb">
                        {folderStack.length > 1 && (
                            <button
                                type="button"
                                className="game-edit-back-button"
                                onClick={handleBack}
                            >
                                ← Back
                            </button>
                        )}
                        <span className="game-edit-breadcrumb-text">{breadcrumb}</span>
                    </div>
                    <div className="game-edit-actions">
                        <button
                            type="button"
                            className="game-edit-button primary"
                            onClick={() => openCreateModal('folder')}
                        >
                            Add Folder
                        </button>
                        <button
                            type="button"
                            className="game-edit-button secondary"
                            onClick={() => openCreateModal('card')}
                        >
                            Add Card
                        </button>
                    </div>
                </div>

                <div className="game-edit-grid">
                    {currentFolder && currentFolder.children && currentFolder.children.length > 0 ? (
                        currentFolder.children.map((item) => (
                            <div
                                key={item.id}
                                className={`game-edit-item game-edit-item-${item.type}`}
                                draggable
                                onDragStart={() => handleDragStart(item.id)}
                                onDragEnd={handleDragEnd}
                                onClick={
                                    item.type === 'folder'
                                        ? () => handleOpenFolder(item.id)
                                        : undefined
                                }
                                onDragOver={
                                    item.type === 'folder'
                                        ? handleFolderDragOver
                                        : undefined
                                }
                                onDrop={
                                    item.type === 'folder'
                                        ? (e) => handleDropOnFolder(e, item.id)
                                        : undefined
                                }
                            >
                                <div className="game-edit-item-header">
                                    <span className="game-edit-item-label">
                                        {item.type === 'folder' ? 'Folder' : 'Card'}
                                    </span>
                                    <div className="game-edit-item-actions">
                                        <button
                                            type="button"
                                            className="game-edit-item-edit-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openEditModal(item);
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="game-edit-item-delete-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteItem(item.id);
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                                <div className="game-edit-item-title">{item.type === 'folder' ? item.title : item.reference}</div>
                                {item.type === 'folder' && (
                                    <div className="game-edit-item-meta">
                                        Category: {item.category || 'None'}
                                    </div>
                                )}
                                {item.type === 'card' && (
                                    <div className="game-edit-item-meta">
                                        {item.paraphrase && (
                                            <div className="game-edit-item-paraphrase">
                                                {item.paraphrase}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="game-edit-empty">No folders or cards here yet. Use the buttons above to add some.</div>
                    )}
                </div>

                {modalOpen && (
                    <div className="game-edit-modal-overlay">
                        <div className="game-edit-modal">
                            <div className="game-edit-modal-header">
                                <h2>
                                    {editingId
                                        ? modalMode === 'folder'
                                            ? 'Edit Folder'
                                            : 'Edit Card'
                                        : modalMode === 'folder'
                                            ? 'Add Folder'
                                            : 'Add Card'}
                                </h2>
                                <button
                                    type="button"
                                    className="game-edit-modal-close"
                                    onClick={closeModal}
                                >
                                    ×
                                </button>
                            </div>

                            <form className="game-edit-form" onSubmit={handleSubmit}>
                                {modalMode === 'folder' && (
                                    <>
                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Title
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="game-edit-input"
                                                    value={formValues.title}
                                                    onChange={handleFormChange}
                                                    placeholder="Folder title"
                                                />
                                            </label>
                                        </div>
                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Category
                                                <select
                                                    name="category"
                                                    className="game-edit-input"
                                                    value={formValues.category}
                                                    onChange={handleFormChange}
                                                >
                                                    {CATEGORY_OPTIONS.map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </div>
                                    </>
                                )}

                                {modalMode === 'card' && (
                                    <>
                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Reference
                                                <input
                                                    type="text"
                                                    name="reference"
                                                    className="game-edit-input"
                                                    value={formValues.reference}
                                                    onChange={handleFormChange}
                                                    placeholder="e.g., Matthew 16:16"
                                                />
                                            </label>
                                        </div>

                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Verse (NAB)
                                                <textarea
                                                    name="verse"
                                                    className="game-edit-textarea"
                                                    value={formValues.verse}
                                                    onChange={handleFormChange}
                                                    placeholder="Paste the verse text here"
                                                />
                                            </label>
                                        </div>

                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Fake Verse (optional)
                                                <textarea
                                                    name="fakeVerse"
                                                    className="game-edit-textarea"
                                                    value={formValues.fakeVerse}
                                                    onChange={handleFormChange}
                                                    placeholder="Optional decoy verse to use as a wrong answer"
                                                />
                                            </label>
                                        </div>

                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Paraphrase
                                                <textarea
                                                    name="paraphrase"
                                                    className="game-edit-textarea"
                                                    value={formValues.paraphrase}
                                                    onChange={handleFormChange}
                                                    placeholder="Short paraphrase / gist of the verse"
                                                />
                                            </label>
                                        </div>

                                        <div className="game-edit-form-row">
                                            <label className="game-edit-label">
                                                Hint / Explanation (optional)
                                                <textarea
                                                    name="hint"
                                                    className="game-edit-textarea"
                                                    value={formValues.hint}
                                                    onChange={handleFormChange}
                                                    placeholder="Quick reminder that can show during the game (e.g. how this is used in argument)"
                                                />
                                            </label>
                                        </div>
                                    </>
                                )}

                                <div className="game-edit-form-actions">
                                    {editingId && parentFolder && (
                                        <button
                                            type="button"
                                            className="game-edit-button secondary"
                                            onClick={handleMoveUpOneLevel}
                                        >
                                            Move to {parentFolder.title || 'parent folder'}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="game-edit-button secondary"
                                        onClick={closeModal}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="game-edit-button primary">
                                        {editingId ? 'Save' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default GameEdit;